import { AccountType } from '@openx/types/account.ts';
import type { AppInventoryOptionType } from '@openx/types/targeting/appInventory.ts';
import {
  type AppBundleIdOptionPayload,
  CustomBlockingRulesOptions,
  type InstancesByIdObject,
  type IntersectOptionPayload,
  type InventoryAndContentApi,
  type InventoryAndContentApiRequest,
  type InventoryAndContentState,
  type InventoryContentIdsTypeOption,
  InventoryContentOption,
  type InventoryOptionPayloadApiFormat,
  type InventoryTargetingContent,
  type PublisherInventoryRule,
  type PublisherInventoryRuleApi,
  type PublisherInventoryRuleOptions,
  type TargetableInventory,
  type TargetableInventoryEntities,
  type TargetableInventoryExcludes,
  type TargetableInventoryList,
  TargetableInventoryOption,
  TargetableInventoryParentSearch,
  TargetableInventoryType,
} from '@openx/types/targeting/inventoryAndContent.ts';
import type { ComparisonList, ComparisonMap, CriteriaValue } from '@openx/types/targeting/targetingValuesTypes.ts';
import { ComparisonType, Criteria, Intersect } from '@openx/types/targeting/targetingValuesTypes.ts';
import type { TFunction } from 'i18next';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import omitBy from 'lodash/omitBy';
import type { InvalidItemsError, ValidationResult } from '../BulkAdd/types.ts';
import { ID_REGEX, INSTANCE_HASH_REGEX } from './constants.ts';
import { getEmptyInventoryState } from './inventoryHelpers.ts';

export function getInventoryDisplayName(inventory: TargetableInventory, noParent?: boolean): string {
  const type = inventory.type;
  const parentName = noParent ? '' : `${inventory.parent_name} > `;

  switch (type) {
    case TargetableInventoryType.ADUNIT:
      return `${parentName}${inventory.name} (Ad Unit)`;
    case TargetableInventoryType.SITESECTION:
      return `${parentName}${inventory.name} (Section)`;
    case TargetableInventoryType.ADUNITGROUP:
      return `${parentName}${inventory.name} (Ad Unit Group)`;
    case TargetableInventoryType.SITE:
      return `${inventory.name} (Site)`;
    case TargetableInventoryType.ACCOUNT:
      if (inventory.type_full === AccountType.AD_NETWORK) {
        return `${inventory.name} (Network)`;
      }
      return `${inventory.name} (Publisher)`;
    default:
      return `${inventory.name} (unknown type)`;
  }
}

export const mapInventoriesListToApiFormat = (list: TargetableInventoryList): InventoryOptionPayloadApiFormat => {
  const data: InventoryOptionPayloadApiFormat = {};

  for (const item of list) {
    let type = item.type as string;
    if (item.type_full === AccountType.AD_NETWORK) {
      type = TargetableInventoryType.NETWORK;
    }
    if (item.type === TargetableInventoryType.ADUNITGROUP) {
      type = 'page';
    }
    data[type] = data[type] ? `${data[type]},${item.id}` : item.id;
  }

  return data;
};

export const prepareInstancesById = (
  includes: TargetableInventoryList,
  excludes: TargetableInventoryList
): InstancesByIdObject => {
  const includesPart = prepareIdByInstancesForInventoryList(includes, 'includes');
  const excludesPart = prepareIdByInstancesForInventoryList(excludes, 'excludes');

  return merge(includesPart, excludesPart);
};

const prepareIdByInstancesForInventoryList = (list: TargetableInventoryList, option: string): InstancesByIdObject => {
  const data: InstancesByIdObject = {};

  for (const item of list) {
    let type = item.type as string;
    const instanceId = item.instance_uid as string;
    if (item.type_full === AccountType.AD_NETWORK) {
      type = TargetableInventoryType.NETWORK;
    }

    if (data[instanceId]) {
      data[instanceId][option][type] = data[instanceId][option][type]
        ? `${data[instanceId][option][type]},${item.id}`
        : item.id;
    } else {
      data[instanceId] = { [option]: {} };
      data[instanceId][option][type] = data[instanceId][option][type]
        ? `${data[instanceId][option][type]},${item.id}`
        : item.id;
    }
  }
  return data;
};

export const transformPublisherInventoryRulesApiPayload = (
  rulesApiData: InventoryAndContentApi['publisher_inventory_rules']
): PublisherInventoryRule[] => {
  if (!rulesApiData?.length) return [];

  return rulesApiData.reduce<PublisherInventoryRule[]>((rulesArray, currentRule) => {
    const isBundleIdRule = !!currentRule?.bundle_id?.length;
    const isDomainRule = !!currentRule?.domain?.length;
    const isSubdomainRule = !!currentRule?.subdomain?.length;

    if (isBundleIdRule && (isDomainRule || isSubdomainRule)) {
      rulesArray.push(
        {
          ...currentRule,
          blockingRuleOption: CustomBlockingRulesOptions.BUNDLE_ID,
          domain: [],
          subdomain: [],
        },
        {
          ...currentRule,
          blockingRuleOption: CustomBlockingRulesOptions.DOMAIN,
          bundle_id: [],
        }
      );
    } else {
      rulesArray.push({
        ...currentRule,
        blockingRuleOption: isBundleIdRule ? CustomBlockingRulesOptions.BUNDLE_ID : CustomBlockingRulesOptions.DOMAIN,
      });
    }

    return rulesArray;
  }, []);
};

export const transformAppGroupFromApiToState = (
  appGroupData: { op: string; val: string | string[] },
  appGroupsMap: Map<string, string> | undefined
) => {
  const comparisonType =
    appGroupData.op === Intersect.INTERSECTS ? ComparisonType.EQUALS : ComparisonType.DOES_NOT_EQUAL;

  const values = typeof appGroupData.val === 'string' ? appGroupData.val.split(',') : appGroupData.val;

  const enrichedValues = appGroupsMap
    ? values.map(v => {
        if (v.includes(':')) return v;
        const name = appGroupsMap.get(v);
        return name ? `${v}:${name}` : v;
      })
    : values;

  return {
    comparisonType,
    enrichedValues,
  };
};

export const transformAppGroupFromStateToApi = (data: { op: CriteriaValue; val?: ComparisonMap }) => {
  const valEntries = Object.entries(data?.val || {});
  const nonEmptyEntries = valEntries.filter(
    (entry): entry is [string, string[]] => Array.isArray(entry[1]) && entry[1].length > 0
  );

  for (const [comparisonOp, values] of nonEmptyEntries) {
    const apiOp = comparisonOp === ComparisonType.EQUALS ? Intersect.INTERSECTS : Intersect.NOT_INTERSECTS;

    return {
      op: apiOp,
      val: values.map(v => getAppGroupId(v)).join(','),
    };
  }

  return null;
};

export const transformAppBundleIdFromApiToState = data => {
  return {
    op: data.op,
    ...(data?.val?.length ? { val: mapByComparison(data.val) } : {}),
    ...(data?.inventorylist?.id ? { inventorylist_id: data.inventorylist.id } : {}),
  };
};

export const transformAppBundleIdFromStateToApi = (data: AppBundleIdOptionPayload) => {
  const hasVal = Object.values(data?.val || []).some(array => Array.isArray(array) && array.length > 0);

  if (hasVal || data?.inventorylist_id) {
    return {
      op: data.op,
      ...(hasVal ? { val: comparisonMapToList(data.val || {}) } : {}),
      ...(data.inventorylist_id
        ? {
            inventorylist: {
              id: data.inventorylist_id,
              type: 'allow' as const,
            },
          }
        : {}),
    };
  }

  return null;
};

export const inventoryApiToState = (
  inventoryApiData: InventoryAndContentApi | null | undefined,
  appGroupsMap?: Map<string, string>
): InventoryAndContentState => {
  const initState: InventoryAndContentState = getEmptyInventoryState();

  if (!inventoryApiData) {
    return initState;
  }

  Object.keys(inventoryApiData).forEach(contentOption => {
    const data = inventoryApiData[contentOption];

    if (!data) {
      return;
    }

    switch (contentOption) {
      case InventoryContentOption.CONTENT_INTER_DIMENSION_OPERATOR: {
        initState[contentOption] = data;
        break;
      }
      case InventoryContentOption.ADUNIT:
      case InventoryContentOption.ADUNIT_SIZE:
      case InventoryContentOption.PUBLISHER_ID:
      case InventoryContentOption.INSTANCE_HASH:
      case InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID:
      case InventoryContentOption.SITE: {
        initState[contentOption] = {
          op: data.op,
          val: data.val.split(','),
        };
        break;
      }
      case InventoryContentOption.KEYWORDS: {
        initState[contentOption] = {
          op: data.op,
          val: data.val,
        };
        break;
      }
      case InventoryContentOption.INVENTORY:
        initState[InventoryContentOption.INVENTORY][TargetableInventoryOption.INCLUDES] = [];
        initState[InventoryContentOption.INVENTORY][TargetableInventoryOption.EXCLUDES] = [];
        break;
      case InventoryContentOption.APP_BUNDLE_ID: {
        if (data?.val?.length || data?.inventorylist) {
          initState[contentOption] = transformAppBundleIdFromApiToState(data);
        }
        break;
      }
      case InventoryContentOption.PUBLISHER_INVENTORY_RULES: {
        initState[contentOption] = transformPublisherInventoryRulesApiPayload(data);
        break;
      }
      case InventoryContentOption.APP_GROUP: {
        const appGroupData = data as { op: string; val: string } | undefined;

        if (appGroupData?.val) {
          const { comparisonType, enrichedValues } = transformAppGroupFromApiToState(appGroupData, appGroupsMap);

          initState[contentOption] = {
            op: appGroupData.op === Intersect.INTERSECTS ? Criteria.ANY : Criteria.ALL,
            val: { [comparisonType]: enrichedValues },
          };
        }
        break;
      }
      default: {
        if (data.op && Array.isArray(data.val)) {
          initState[contentOption] = {
            op: data.op,
            val: mapByComparison(data.val),
          };
        }
        break;
      }
    }
  });

  return initState;
};

export const transformPublisherInventoryRulesPayload = (
  publisherInventoryRules: PublisherInventoryRule[]
): InventoryAndContentApi['publisher_inventory_rules'] => {
  const rulesMap: Record<string, PublisherInventoryRuleApi> = {};

  for (const currentRule of publisherInventoryRules) {
    // exclude blockingRuleOption from the api payload
    const { blockingRuleOption: _, ...sanitizedRule } = currentRule;
    const key = currentRule.publisher_id;

    if (rulesMap[key]) {
      const nonEmptyValuesFromCurrentRule = omitBy(sanitizedRule, isEmpty);
      rulesMap[key] = { ...rulesMap[key], ...nonEmptyValuesFromCurrentRule };
    } else {
      rulesMap[key] = sanitizedRule;
    }
  }

  return Object.values(rulesMap);
};

export const inventoryStateToApi = (
  inventoryState: InventoryAndContentState,
  addIdByInstances: boolean
): InventoryAndContentApiRequest | null => {
  const apiData: InventoryAndContentApiRequest = {};

  const includes = inventoryState[InventoryContentOption.INVENTORY][TargetableInventoryOption.INCLUDES];
  const excludes = inventoryState[InventoryContentOption.INVENTORY][TargetableInventoryOption.EXCLUDES];
  const appendInventories = !isEmpty(includes) || !isEmpty(excludes);
  const setContentOperatorToNull =
    isEmpty(inventoryState[InventoryContentOption.ADUNIT].val) &&
    isEmpty(inventoryState[InventoryContentOption.ADUNIT_SIZE].val) &&
    isEmpty(inventoryState[InventoryContentOption.APP_BUNDLE_ID].val) &&
    !inventoryState[InventoryContentOption.APP_BUNDLE_ID].inventorylist_id &&
    !appendInventories &&
    isEmpty(inventoryState[InventoryContentOption.PAGE_URL].val) &&
    isEmpty(inventoryState[InventoryContentOption.KEYWORDS].val) &&
    isEmpty(inventoryState[InventoryContentOption.PUBLISHER_ID].val) &&
    isEmpty(inventoryState[InventoryContentOption.INSTANCE_HASH].val) &&
    isEmpty(inventoryState[InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID].val) &&
    isEmpty(inventoryState[InventoryContentOption.SITE].val) &&
    isEmpty(inventoryState[InventoryContentOption.APP_GROUP]?.val);

  Object.keys(inventoryState).forEach(contentOption => {
    const data = inventoryState[contentOption];

    switch (contentOption) {
      case InventoryContentOption.PUBLISHER_ID:
      case InventoryContentOption.INSTANCE_HASH:
      case InventoryContentOption.ADUNIT:
      case InventoryContentOption.ADUNIT_SIZE:
      case InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID:
      case InventoryContentOption.SITE: {
        if (data.val.length) {
          apiData[contentOption] = {
            op: data.op,
            val: data.val.join(','),
          };
        }
        break;
      }
      case InventoryContentOption.CONTENT_INTER_DIMENSION_OPERATOR: {
        apiData[contentOption] = setContentOperatorToNull ? null : data;
        break;
      }
      case InventoryContentOption.KEYWORDS: {
        if (data.val.length) {
          apiData[contentOption] = {
            op: data.op,
            val: data.val,
          };
        }
        break;
      }
      case InventoryContentOption.APP_BUNDLE_ID: {
        const appBundleIdState = data as AppBundleIdOptionPayload;
        const transformedData = transformAppBundleIdFromStateToApi(appBundleIdState);

        if (transformedData) {
          apiData[contentOption] = transformedData;
        }
        break;
      }

      case InventoryContentOption.PUBLISHER_INVENTORY_RULES: {
        if (data?.length) {
          apiData[contentOption] = transformPublisherInventoryRulesPayload(data);
        }
        break;
      }

      case InventoryContentOption.APP_GROUP: {
        const transformedData = transformAppGroupFromStateToApi(data);

        if (transformedData) {
          apiData[contentOption] = transformedData;
        }
        break;
      }

      case InventoryContentOption.INVENTORY:
        if (appendInventories) {
          apiData[TargetableInventoryOption.INCLUDES] = mapInventoriesListToApiFormat(includes);
          apiData[TargetableInventoryOption.EXCLUDES] = mapInventoriesListToApiFormat(excludes);
          if (addIdByInstances)
            apiData[TargetableInventoryOption.ID_BY_INSTANCES] = prepareInstancesById(includes, excludes);
        }
        break;

      default: {
        const itemsList = comparisonMapToList(data.val);
        // only add content option if it contains any items
        if (itemsList.length) {
          apiData[contentOption] = {
            op: data.op,
            val: comparisonMapToList(data.val),
          };
        }
        break;
      }
    }
  });

  const inventoryAndContentKeys = Object.keys(apiData);
  const removeContentInterDimOperator =
    inventoryAndContentKeys.length === 1 &&
    inventoryAndContentKeys[0] === InventoryContentOption.CONTENT_INTER_DIMENSION_OPERATOR;

  if (removeContentInterDimOperator) {
    inventoryAndContentKeys.pop();
  }

  return inventoryAndContentKeys.length === 0 ? null : apiData;
};

export function groupExcludes(excludesList: TargetableInventoryList): TargetableInventoryExcludes {
  const typesToGroup: string[] = [
    TargetableInventoryType.ADUNIT,
    TargetableInventoryType.ADUNITGROUP,
    TargetableInventoryType.SITESECTION,
  ];

  const { itemsToGroup, other } = excludesList.reduce<Record<string, TargetableInventoryList>>(
    (result, element) => {
      if (typesToGroup.includes(element.type)) {
        result.itemsToGroup.push(element);
      } else {
        result.other.push(element);
      }

      return result;
    },
    { itemsToGroup: [], other: [] }
  );

  const grouped = itemsToGroup.reduce<Record<string, TargetableInventoryList>>((result, item) => {
    result[`${item.parent_name}`] = [...(result[`${item.parent_name}`] || []), item];

    return result;
  }, {});

  return { grouped, other };
}

export const getGroupedLimit = (
  grouped: Record<string, TargetableInventoryList>,
  rowsLimit: number
): { groupedDisplayLimit: number; groupedRowsUsed: number; isMoreGroupedToDisplay: boolean } => {
  let itemsCount = 0;
  let keysCount = 0;
  let groupedRowsUsed = 0;

  for (const key in grouped) {
    keysCount++;
    itemsCount += grouped[key].length;
    groupedRowsUsed = itemsCount + keysCount;

    if (groupedRowsUsed > rowsLimit) {
      const groupedDisplayLimit = keysCount > 1 ? keysCount - 1 : 1;
      return { groupedDisplayLimit, groupedRowsUsed, isMoreGroupedToDisplay: true };
    }
  }

  return { groupedDisplayLimit: keysCount, groupedRowsUsed, isMoreGroupedToDisplay: false };
};

export const getExtendQuery = (includes: TargetableInventoryList): string => {
  if (!includes.length) {
    return '';
  }

  const data: TargetableInventoryEntities = {};

  for (const item of includes) {
    if (TargetableInventoryParentSearch[item.type]) {
      data[item.type] = data[item.type] ? `${data[item.type]} OR ${item.uid}` : item.uid;
    }
  }

  const query = Object.entries(data)
    .map(([type, values]) => {
      if (values.includes('OR')) {
        values = `(${values})`;
      }
      return `${TargetableInventoryParentSearch[type]}:${values}`;
    })
    .join(' OR ');

  return `AND (${query})`;
};

export const splitIncludedExcluded = (
  inventories: TargetableInventoryList[],
  excludes: TargetableInventoryEntities
): InventoryTargetingContent<TargetableInventoryList> => {
  const inventoriesAll = [...inventories].flat();

  const excludedIdsArray: Array<string> = Object.values(excludes).length
    ? Object.values(excludes).flatMap(excludesGroup => excludesGroup.split(','))
    : [];

  const splitedIncludedExcludedObject: InventoryTargetingContent<TargetableInventoryList> = {
    excludes: [],
    includes: [],
  };
  inventoriesAll.forEach(inventory => {
    const key = excludedIdsArray.includes(inventory.id) ? 'excludes' : 'includes';
    splitedIncludedExcludedObject[key] = [...splitedIncludedExcludedObject[key], inventory];
  });

  return splitedIncludedExcludedObject;
};

export const mapByComparison = (opsList: ComparisonList): ComparisonMap => {
  const mappedOps = {};
  opsList.forEach(el => {
    mappedOps[el.op] ??= [];
    mappedOps[el.op].push(el.val);
  });
  return mappedOps;
};

export const comparisonMapToList = (optionsMap: ComparisonMap): ComparisonList => {
  const opsList: ComparisonList = [];

  Object.entries(optionsMap).forEach(([op, values]) => {
    values?.forEach(val => {
      opsList.push({
        op: op as ComparisonType,
        val,
      });
    });
  });
  return opsList;
};

// Extracts display label from "id:name" format
export const getAppGroupDisplayLabel = (value: string): string => {
  const colonIndex = value.indexOf(':');
  return colonIndex !== -1 ? value.substring(colonIndex + 1) : value;
};

// Extracts ID from "id:name" format
export const getAppGroupId = (value: string): string => {
  const colonIndex = value.indexOf(':');
  return colonIndex !== -1 ? value.substring(0, colonIndex) : value;
};

export function getBulkPlaceholder(t: TFunction, type: InventoryContentOption | AppInventoryOptionType, label: string) {
  switch (type) {
    case InventoryContentOption.INSTANCE_HASH:
      return t(
        `Instance Hash may be entered as either comma, semicolon, or new line separated values.

Examples:
Instance_#1,Instance_#2
or
Instance_#1;Instance_#2
or
Instance_#1
Instance_#2`
      );
    case InventoryContentOption.PUBLISHER_ID:
      return t(
        `Publisher ID may be entered as either comma, semicolon, or new line separated values.

Examples:
Publisher_id1,Publisher_id2
or
Publisher_id1;Publisher_id2
or
Publisher_id1
Publisher_id2`
      );
    case InventoryContentOption.ADUNIT:
      return t(
        `Ad Unit ID may be entered as either comma, semicolon, or new line separated values.

Examples:
AdUnit_id1,AdUnit_id2
or
AdUnit_id1;AdUnit_id2
or
AdUnit_id1
AdUnit_id2`
      );
    case InventoryContentOption.SITE:
      return t(
        `Site ID may be entered as either comma, semicolon, or new line separated values.

Examples:
Site_id1,Site_id2
or
Site_id1;Site_id2
or
Site_id1
Site_id2`
      );
    case InventoryContentOption.KEYWORDS:
      return t(
        `Keywords may be entered as either comma, semicolon, or new line separated values.

Examples:
keyword-one, keyword-two
or
keyword-one;keyword-two
or
keyword-one
keyword-two`
      );
    case InventoryContentOption.PUBLISHER_INVENTORY_RULES:
      return t(
        `Domains may be entered as either comma, semicolon, or new line separated values.

Examples:
example.com,site.eu
or
example.com;site.eu
or
example.com
site.eu
`
      );
    default:
      return t(
        `{label} may be entered as either comma, semicolon, or new line separated values.

Examples:
example.com,site.eu
or
example.com;site.eu
or
example.com
site.eu`,
        { label }
      );
  }
}

export function getTypeInventoryIDsPlaceholder(
  t: TFunction<'translation', undefined>,
  type: InventoryContentIdsTypeOption
) {
  return {
    [InventoryContentOption.INSTANCE_HASH]: t('Enter an Instance Hash and press Enter'),
    [InventoryContentOption.PUBLISHER_ID]: t('Enter a Publisher ID and press Enter'),
    [InventoryContentOption.ADUNIT]: t('Enter a Ad Unit ID and press Enter'),
    [InventoryContentOption.SITE]: t('Enter a Site ID and press Enter'),
    [InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID]: t('Enter a App Bundle ID and press Enter'),
  }[type];
}

export const validateBulkInventoryIds = (
  values: string[],
  currentSet: Pick<IntersectOptionPayload, 'val'>,
  type: InventoryContentIdsTypeOption,
  customValidationMethod?: (
    value: string,
    currentSet: Set<string>
  ) => { error: boolean; errorMessage: string } | undefined
): Promise<ValidationResult> => {
  const errors: InvalidItemsError[] = [];
  const validItems: string[] = [];
  const inventoryIDsSet = new Set(currentSet.val);

  values.forEach(value => {
    const validationResult = customValidationMethod
      ? customValidationMethod(value, inventoryIDsSet)
      : validateSingleRecordSubmissionInventoryIDs(value, inventoryIDsSet, type);

    if (validationResult?.error) {
      const errorObject = errors.find(singleError => singleError.error === validationResult.errorMessage);

      if (errorObject) {
        errorObject.invalidItems.push(value);
      } else {
        errors.push({ error: validationResult.errorMessage, invalidItems: [value] });
      }
    } else {
      validItems.push(value);
    }
  });

  return new Promise<ValidationResult>(res => res({ errors, validItems }));
};

export function validateSingleRecordSubmissionInventoryIDs(
  value = '',
  currentSet: Set<string>,
  type: InventoryContentIdsTypeOption
): { error: boolean; errorMessage: string } | undefined {
  value = value.trim();

  if (!value) {
    return { error: true, errorMessage: 'This field cannot be empty.' };
  }

  const alreadyExists = currentSet.has(value);

  if (alreadyExists) {
    return { error: true, errorMessage: 'This item is already on the list.' };
  }

  if (type === InventoryContentOption.INSTANCE_HASH && !value.match(INSTANCE_HASH_REGEX)) {
    return { error: true, errorMessage: 'Please enter a valid Instance Hash' };
  }

  if (!value.match(ID_REGEX)) {
    switch (type) {
      case InventoryContentOption.PUBLISHER_ID:
        return { error: true, errorMessage: 'Please enter a valid Publisher ID' };
      case InventoryContentOption.SITE:
        return { error: true, errorMessage: 'Please enter a valid Site ID' };
      case InventoryContentOption.ADUNIT:
        return { error: true, errorMessage: 'Please enter a valid Ad Unit ID' };
    }
  }

  currentSet.add(value);
}

export const updateAppBundleIdState = (
  appBundleIdState: AppBundleIdOptionPayload,
  payload: {
    values?: string[] | AppBundleIdOptionPayload['val'];
    comparisonType?: string;
    inventorylist_id?: string | null;
  }
): void => {
  const { values, comparisonType, inventorylist_id } = payload;

  if (inventorylist_id) {
    appBundleIdState.inventorylist_id = inventorylist_id;
  }

  if (inventorylist_id === null) {
    delete appBundleIdState.inventorylist_id;
  }

  if (Array.isArray(values) && values.length && comparisonType) {
    if (appBundleIdState.val === undefined) {
      appBundleIdState.val = {
        [comparisonType]: values,
      };
      return;
    }

    appBundleIdState.val[comparisonType] = values;
  }

  if (Array.isArray(values) && !values.length && comparisonType && appBundleIdState?.val?.[comparisonType]) {
    delete appBundleIdState.val[comparisonType];
  }

  if (!Array.isArray(values) && values) {
    appBundleIdState.val = values;
  }
};

export const validateBulkDomains = (
  domainsList: string[],
  publisherInventoryRuleOptions: PublisherInventoryRuleOptions,
  currentBlockingRules: InventoryAndContentState[typeof InventoryContentOption.PUBLISHER_INVENTORY_RULES],
  validateDomain: (domain: string) => string
): Promise<ValidationResult> => {
  const currentDomainsList = currentBlockingRules.find(
    rule =>
      rule.publisher_id === publisherInventoryRuleOptions.publisherID &&
      rule.blockingRuleOption === CustomBlockingRulesOptions.DOMAIN
  );

  const added = new Set(currentDomainsList ? [...currentDomainsList.domain, ...currentDomainsList.subdomain] : []);

  const duplicates: string[] = [];
  const invalid: string[] = [];
  const validItems: string[] = [];

  domainsList.forEach(domain => {
    if (validateDomain(domain)) {
      invalid.push(domain);
    } else if (added.has(domain)) {
      duplicates.push(domain);
    } else {
      validItems.push(domain);
      added.add(domain);
    }
  });

  const errors: InvalidItemsError[] = [];

  if (duplicates.length) errors.push({ error: 'This item is already on the list.', invalidItems: duplicates });
  if (invalid.length) errors.push({ error: 'Invalid Domains.', invalidItems: invalid });

  return new Promise<ValidationResult>(res => res({ errors, validItems }));
};

export const validateIfPublisherIdIsUsed = (value: string, blockedPublisherIds?: Set<string>) => {
  const isPublisherIdUsed = blockedPublisherIds?.has(value);

  if (isPublisherIdUsed) {
    return { error: true, errorMessage: 'Provided Publisher ID is already blocked.' };
  }

  return { error: false, errorMessage: '' };
};

export const validateBulkItemsWithInitialValue = (
  itemsList: string[],
  type: InventoryContentIdsTypeOption
): Promise<ValidationResult> => {
  const added = new Set<string>();
  const duplicates: string[] = [];
  const invalid: string[] = [];
  const validItems: string[] = [];
  const errors: InvalidItemsError[] = [];

  const errorMessages: Record<string, string> = {
    [InventoryContentOption.INSTANCE_HASH]: 'Please enter a valid Instance Hash',
    [InventoryContentOption.PUBLISHER_ID]: 'Please enter a valid Publisher ID',
    [InventoryContentOption.SITE]: 'Please enter a valid Site ID',
  };

  const isNotAppBundleOrInstanceHash =
    type !== InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID && type !== InventoryContentOption.INSTANCE_HASH;

  itemsList.forEach(item => {
    if (added.has(item)) {
      duplicates.push(item);
      return;
    }

    if (
      (type === InventoryContentOption.INSTANCE_HASH && !item.match(INSTANCE_HASH_REGEX)) ||
      (isNotAppBundleOrInstanceHash && !item.match(ID_REGEX))
    ) {
      invalid.push(item);
      return;
    }

    validItems.push(item);
    added.add(item);
  });

  if (duplicates.length) errors.push({ error: 'This item is already on the list.', invalidItems: duplicates });
  if (invalid.length) errors.push({ error: errorMessages[type], invalidItems: invalid });

  return new Promise<ValidationResult>(res => res({ errors, validItems }));
};
